import React, { useEffect, useRef, useState } from "react";
import invoke from "../../common/utils/invoke";
import Stepper from "../../common/utils/Stepper";
import FormInput from "../../common/components/FormInput";
import CustomDatePicker from "../../common/components/DatePicker";
import BrandLoader from "../../common/components/BrandLoader.jsx";
import Modal from "../../common/components/Modal.jsx";
import { logError } from "../../common/utils/logger.js";
import "./EcsPremiumOffsetConsentForm.scss";
import { PREMIUMOFFSET_ERRORMSG } from "../../constants.js";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;
const BRAND_COLOR = "#97262A";

/** TEMPORARY frontend-only bypass when policy has no registered mobile. Remove when OTP/SMS is available. */
const TEMP_OTP_BYPASS_CODE = "123456";

const STEPS = ["Validate", "Verify", "Consent"];

const STEP_META = {
  step1: {
    eyebrow: "Premium offset consent",
    title: "Verify your policy to begin",
    lede:
      "Enter your policy number and date of birth. This journey is available for Guaranteed Advantage Income Plan (GAIN) policies that are in force.",
  },
  step2: {
    eyebrow: "Step 2 of 3 · Verify",
    title: "Enter the code we sent you",
    lede:
      "For your security, confirm the 6-digit one-time password sent to the mobile number registered against your policy.",
  },
  step3: {
    eyebrow: "Step 3 of 3 · Consent",
    title: "Review your premium offset",
    lede:
      "Your Guaranteed Income can be applied against your upcoming renewal premium, so only the residual amount is collected through your ECS mandate.",
  },
};

const INITIAL_FORM_DATA = { policyNumber: "", dob: "" };

/** Formats a Date object as DD-MM-YYYY for the backend. */
const formatDobForBackend = (date) => {
  if (!date) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getFullYear()}`;
};

/** Masks a mobile number, keeping only the last 4 digits visible (e.g. ••••••3210). */
const maskMobile = (mobile) => {
  const digits = (mobile || "").replace(/\D/g, "");
  if (digits.length < 4) return "••••••";
  return `••••••${digits.slice(-4)}`;
};

const toNumber = (value) => {
  const n = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/** Parses a DD-MM-YYYY string into a local midnight Date, or null if invalid. */
const parseDdMmYyyy = (value) => {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(String(value ?? "").trim());
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** True when `dateStr` (DD-MM-YYYY) is within `days` days of today, in either direction. */
const isWithinDaysOfToday = (dateStr, days) => {
  const target = parseDdMmYyyy(dateStr);
  if (!target) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
  return Math.abs(diffDays) <= days;
};

const ALREADY_CONSENTED_MESSAGE =
  "Please note that we already have your consent available with us. Thank you for placing your request once again.";
const NEAR_DEBIT_DATE_MESSAGE =
  "Since we are close to the scheduled ECS debit date, this consent might not apply to current payment cycle. In case of full payment deduction, eligible cashback amount will be paid in 7 working days.";

/**
 * Normalizes a raw gainPolicyValidation response row (Contract_Type, Policy_Status, DOB,
 * Mobile_Number, Product_Name, Paid_To_Date, Frequency, ECS_Active, Client_Id, Actual_Debit_Date)
 * into the shape the UI needs. The API doesn't return policyholder name or a premium/income/residual
 * breakdown, so those render blank/zero.
 * @param {Object} table - one row of the API's `Response` array
 * @param {string} policyNumberFallback - the policy number the user submitted (API doesn't echo it back)
 */
const normalizeOffsetTable = (table, policyNumberFallback) => {
  const policyStatusRaw = String(table?.Policy_Status ?? "").trim().toUpperCase();
  const ecsActive =
    String(table?.ECS_Active ?? "").trim() !== "" &&
    String(table?.ECS_Active ?? "").trim() !== "0";

  return {
    policyholderName: "",
    policyNumber: policyNumberFallback ?? "",
    planName: table?.Product_Name ?? "",
    policyStatus: policyStatusRaw === "IF" ? "In force" : (table?.Policy_Status ?? "In force"),
    nextPremiumDate: table?.Actual_Debit_Date || "",
    mobile: String(table?.Mobile_Number ?? "")
      .replace(/\D/g, "")
      .slice(-10),
    premiumPayable: 0,
    incomeApplied: 0,
    residualAmount: 0,
    // POfFlag comes only from premiumOffsetPolicyInfo; blank until then (treated as not eligible).
    pofFlag: "",
    alreadyConsented: false,
    ecsActive,
    frequency: table?.Frequency ?? "",
    clientId: table?.Client_Id ?? "",
  };
};

/**
 * Merges a premiumOffsetPolicyInfo response (contractPremium, EGIAmt, amountDue, POfFlag, ownerName,
 * contractStatus, ownerClientid) into an already-normalized policy snapshot, filling in the real
 * premium breakdown and existing-consent flag that gainPolicyValidation doesn't provide.
 * NOTE: nextPremiumDate is deliberately left untouched here - it stays as the Actual_Debit_Date
 * captured in step 1. This API's `paidToDate` is a different thing (date already paid through, not
 * the next scheduled debit date) and must not overwrite it.
 * @param {Object|null} info - raw premiumOffsetPolicyInfo response
 * @param {Object} fallback - normalized policy snapshot to merge onto (e.g. from normalizeOffsetTable)
 */
const normalizePolicyInfo = (rawInfo, fallback) => {
  let info = rawInfo;
  // Backend may send the body as a JSON string or wrapped in Response/data - unwrap to the flat object.
  if (typeof info === "string") {
    try {
      info = JSON.parse(info);
    } catch {
      return fallback;
    }
  }
  const wrapped = info?.Response ?? info?.response ?? info?.data;
  if (wrapped) info = Array.isArray(wrapped) ? wrapped[0] : wrapped;
  if (!info) return fallback;

  const premiumPayable = toNumber(info.contractPremium);
  const incomeApplied = toNumber(info.EGIAmt);
  const residualAmount = toNumber(info.amountDue ?? Math.max(premiumPayable - incomeApplied, 0));
  const pofFlag = String(info.POfFlag ?? "").trim().toUpperCase();
  const alreadyConsented = pofFlag === "Y";
  const policyholderName = String(info.ownerName ?? info.lifeAssuredName ?? "").trim();
  const policyStatus = String(info.contractStatus ?? "").trim();

  return {
    ...fallback,
    policyholderName: policyholderName || fallback.policyholderName,
    policyNumber: info.contractNumber ?? fallback.policyNumber,
    policyStatus: policyStatus || fallback.policyStatus,
    clientId: info.ownerClientid ?? info.LAClientid ?? fallback.clientId,
    premiumPayable,
    incomeApplied,
    residualAmount,
    pofFlag,
    alreadyConsented,
  };
};

const EcsPremiumOffsetConsentForm = ({ pageTitle } = {}) => {
  const otpRefs = useRef([]);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [dobDate, setDobDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [alertMsg, setAlertMsg] = useState(null);

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [otpId, setOtpId] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS);
  /** TEMPORARY: true when policy mobile is blank/null — accept TEMP_OTP_BYPASS_CODE without backend OTP APIs. */
  const [otpBypassMode, setOtpBypassMode] = useState(false);

  const [pendingPolicy, setPendingPolicy] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [_otpVerificationToken, setOtpVerificationToken] = useState(null);

  const [agreeChecked, setAgreeChecked] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [srNumber, setSrNumber] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiErrorMsg, setApiErrorMsg] = useState("");
  const [showAlreadyConsentedModal, setShowAlreadyConsentedModal] = useState(false);
  const [showNearDebitDateModal, setShowNearDebitDateModal] = useState(false);

  // Resend OTP countdown - runs while on the verify step
  useEffect(() => {
    if (step !== 2) return undefined;
    if (resendSeconds <= 0) return undefined;
    const timer = setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, resendSeconds]);

  const resetForm = () => {
    setStep(1);
    setFormData(INITIAL_FORM_DATA);
    setDobDate(null);
    setErrors({});
    setAlertMsg(null);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpId(null);
    setOtpError(null);
    setResendSeconds(RESEND_SECONDS);
    setOtpBypassMode(false);
    setPendingPolicy(null);
    setPolicy(null);
    setOtpVerificationToken(null);
    setAgreeChecked(false);
    setShowAlreadyConsentedModal(false);
    setShowNearDebitDateModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (alertMsg) setAlertMsg(null);
  };

  const handleDobChange = (date) => {
    setDobDate(date);
    setFormData((prev) => ({ ...prev, dob: formatDobForBackend(date) }));
    if (errors.dob) setErrors((prev) => ({ ...prev, dob: "" }));
    if (alertMsg) setAlertMsg(null);
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.policyNumber || !formData.policyNumber.trim()) {
      newErrors.policyNumber = "Policy number is required";
    }
    if (!formData.dob || !formData.dob.trim()) {
      newErrors.dob = "Date of birth is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const startOtpJourney = async (mobile) => {
    try {
      const otpResponse = await invoke.generateOTP(mobile);
      if (
        otpResponse?.status !== "success" &&
        otpResponse?.status !== 200
      ) {
        setAlertMsg({
          title: "We couldn't send your OTP",
          desc:
            otpResponse?.error ||
            otpResponse?.message ||
            "OTP generation failed. Please try again later.",
        });
        return false;
      }
      setOtpId(otpResponse?.otpId ?? otpResponse?.id ?? "");
      setOtp(Array(OTP_LENGTH).fill(""));
      setOtpError(null);
      setResendSeconds(RESEND_SECONDS);
      setStep(2);
      return true;
    } catch (err) {
      logError("[EcsPremiumOffsetConsent] generateOTP failed", err);
      setAlertMsg({
        title: "We couldn't send your OTP",
        desc:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "OTP generation failed. Please try again later.",
      });
      return false;
    }
  };

  const handleValidate = async (e) => {
    e.preventDefault();
    setAlertMsg(null);
    if (!validateStep1()) return;

    setLoading(true);
    try {
      const response = await invoke.getEcsPremiumOffsetDetails({
        policyNumber: formData.policyNumber.trim(),
        dob: formData.dob,
      });

      if (!response) {
        setAlertMsg({
          title: PREMIUMOFFSET_ERRORMSG   });
        return;
      }
      if (response.Status && response.Status !== "Success") {
        setAlertMsg({
          title: PREMIUMOFFSET_ERRORMSG   });
        return;
      }

      const table = Array.isArray(response.Response) ? response.Response[0] : null;
      if (!table) {
        setAlertMsg({
          title: PREMIUMOFFSET_ERRORMSG   });
        return;
      }

       if (table?.Policy_Status != "IF") {
        setAlertMsg({
          title: PREMIUMOFFSET_ERRORMSG   });
        return;
      }

      const normalized = normalizeOffsetTable(table, formData.policyNumber.trim());
      const mobileBlank = !normalized.mobile;

      // Partial / invalid mobile (non-blank but not 10 digits) — still block.
      if (!mobileBlank && normalized.mobile.length !== 10) {
        setAlertMsg({
          title: PREMIUMOFFSET_ERRORMSG });
        return;
      }

      setPendingPolicy(normalized);

      // TEMPORARY: blank/null mobile → frontend OTP bypass (123456), no generateOTP call.
      if (mobileBlank) {
        setOtpBypassMode(true);
        setOtpId(null);
        setOtp(Array(OTP_LENGTH).fill(""));
        setOtpError(null);
        setResendSeconds(RESEND_SECONDS);
        setStep(2);
        return;
      }

      setOtpBypassMode(false);
      await startOtpJourney(normalized.mobile);
    } catch (err) {
      logError("[EcsPremiumOffsetConsent] getEcsPremiumOffsetDetails failed", err);
      setAlertMsg({
        title:PREMIUMOFFSET_ERRORMSG});
    } finally {
      setLoading(false);
    }
  };

  // ---------------- OTP ----------------
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    const pos = Number(e.target.dataset.pos);

    setOtp((prev) => {
      const updated = [...prev];
      updated[pos] = value.slice(-1);
      return updated;
    });

    if (value && pos < OTP_LENGTH - 1) {
      otpRefs.current[pos + 1]?.focus();
    }
    if (otpError) setOtpError(null);
  };

  const handleOtpKeyDown = (e) => {
    const pos = Number(e.target.dataset.pos);
    if (e.key === "Backspace" && !otp[pos] && pos > 0) {
      otpRefs.current[pos - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!digits) return;

    const updated = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < digits.length; i += 1) updated[i] = digits[i];
    setOtp(updated);
    otpRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleResendOtp = async () => {
    if (resendSeconds > 0) return;

    // TEMPORARY: bypass mode has no SMS — just reset the OTP inputs / countdown.
    if (otpBypassMode) {
      setOtp(Array(OTP_LENGTH).fill(""));
      setOtpError(null);
      setResendSeconds(RESEND_SECONDS);
      return;
    }

    if (!pendingPolicy?.mobile) return;
    setOtpLoading(true);
    await startOtpJourney(pendingPolicy.mobile);
    setOtpLoading(false);
  };

  const proceedAfterOtpVerified = async () => {
    let finalPolicy = pendingPolicy;
    try {
      const effdate = pendingPolicy.nextPremiumDate || formatDobForBackend(new Date());
      const infoResponse = await invoke.getEcsPremiumOffsetPolicyInfo({
        policyNumber: pendingPolicy.policyNumber,
        effdate,
      });
      finalPolicy = normalizePolicyInfo(infoResponse, pendingPolicy);
    } catch (infoErr) {
      logError("[EcsPremiumOffsetConsent] getEcsPremiumOffsetPolicyInfo failed", infoErr);
    }

    setPolicy(finalPolicy);
    setStep(3);
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== OTP_LENGTH) {
      setOtpError("Enter all six digits, or request a new code.");
      return;
    }
    if (!pendingPolicy) {
      setOtpError("Session expired. Please start again.");
      return;
    }

    setOtpLoading(true);
    setOtpError(null);
    try {
      // TEMPORARY: blank/null mobile — accept fixed OTP on the frontend only (no verifyOTP API).
      if (otpBypassMode) {
        if (enteredOtp !== TEMP_OTP_BYPASS_CODE) {
          setOtpError("That code doesn't match. Please try again.");
          return;
        }
        setOtpVerificationToken(null);
        await proceedAfterOtpVerified();
        return;
      }

      const verifiedSnapshot = {
        policyNo: pendingPolicy.policyNumber,
        mobileNo: pendingPolicy.mobile,
      };

      const verifyResponse = await invoke.verifyOTP({
        otpId,
        identifier: pendingPolicy.mobile,
        otp: enteredOtp,
        verifiedSnapshot,
      });

      if (
        verifyResponse?.status !== "success" &&
        verifyResponse?.verified !== true
      ) {
        throw new Error("Invalid OTP");
      }

      const token = verifyResponse?.verificationToken;
      if (!token || typeof token !== "string" || !token.trim()) {
        throw new Error("Invalid OTP");
      }

      setOtpVerificationToken(token);
      await proceedAfterOtpVerified();
    } catch (err) {
      logError("[EcsPremiumOffsetConsent] verifyOTP failed", err);
      setOtpError("That code doesn't match. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // ---------------- Consent ----------------
  const toggleAgree = () => setAgreeChecked((prev) => !prev);

  const handleSubmitConsent = (e) => {
    e.preventDefault();
    if (!agreeChecked || !policy) return;

    // POfFlag "Y": show already-consented message, then still submit (PDF always goes on final submit).
    if (policy.alreadyConsented) {
      setShowAlreadyConsentedModal(true);
      return;
    }

    // Within 3 days of scheduled ECS debit date - warn, then still submit normally.
    if (isWithinDaysOfToday(policy.nextPremiumDate, 3)) {
      setShowNearDebitDateModal(true);
      return;
    }

    performSubmit();
  };

  const performSubmit = async () => {
    setLoading(true);
    setApiErrorMsg("");
    try {
      // PDF is generated on the backend from premiumOffsetTemplate.pdf using these fields.
      const payload = {
        premiumOffsetType: "createGenericCaseWithAttchment",
        policyNo: policy.policyNumber,
        ownerName: policy.policyholderName || "",
        planName: policy.planName || "",
        mobileNumber: policy.mobile || "",
        amountDue: String(policy.residualAmount ?? ""),
        cashbackAmount: String(policy.incomeApplied ?? ""),
        problem: "test",
        solution: "test",
        attachmentDocName: "ecs_premium_offset_consent.pdf",
        srvcTypeCode: "2117",
        caseChannel: "Website",
      };
      const response = await invoke.submitEcsPremiumOffsetConsent(payload);

      if (!response) {
        setApiErrorMsg("Unable to submit consent right now. Please try again later.");
        setShowErrorModal(true);
        return;
      }
      if (response.Status && response.Status !== "Success") {
        setApiErrorMsg(response.Message || "Submission failed. Please try again.");
        setShowErrorModal(true);
        return;
      }

      const respItem = Array.isArray(response.Response) ? response.Response[0] : response.Response;
      const refNo =
        respItem?.CaseNumber ??
        respItem?.ServiceRequestNumber ??
        respItem?.SRNumber ??
        response?.serviceRequestNumber ??
        response?.srNumber ??
        "";
      setSrNumber(refNo ? String(refNo) : "");
      setShowSuccessModal(true);
    } catch (err) {
      logError("[EcsPremiumOffsetConsent] submit failed", err);
      setApiErrorMsg(
        err?.response?.data?.error ||
          "Submission failed. Please try again.",
      );
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const successMessage = srNumber
    ? `Thank you. Your premium offset consent has been submitted and service request ${srNumber} has been raised.`
    : "Thank you. Your premium offset consent has been submitted and a service request has been raised.";

  const meta =
    step === 1 && pageTitle
      ? { ...STEP_META.step1, title: pageTitle }
      : STEP_META[`step${step}`];
  const currentStepIndex = step - 1;

  return (
    <div className="ecs-consent-wrapper">
      {(loading || otpLoading) && (
        <div className="loader-overlay">
          <BrandLoader color={BRAND_COLOR} loading={loading || otpLoading} size={15} />
        </div>
      )}

      <div className="ecs-consent">
        <Stepper steps={STEPS} currentStep={currentStepIndex} />

        <div className="ecs-consent__card">
          <div className="ecs-consent__header">
            <div className="ecs-consent__eyebrow">{meta.eyebrow}</div>
            <h4 className="ecs-consent__title">{meta.title}</h4>
            <p className="ecs-consent__lede">{meta.lede}</p>
          </div>

          {/* ---------------- STEP 1 : VALIDATE ---------------- */}
          {step === 1 && (
            <form className="ecs-consent__form" onSubmit={handleValidate}>
              <FormInput
                id="policyNumber"
                name="policyNumber"
                label="Policy number"
                value={formData.policyNumber}
                onChange={handleChange}
                placeholder="e.g. 53945178"
                error={errors.policyNumber}
                required
                autoComplete="off"
              />

              <CustomDatePicker
                id="dob"
                name="dob"
                label="Date of birth"
                value={formData.dob ? dobDate : null}
                onChange={handleDobChange}
                placeholder="DD / MM / YYYY"
                maxDate={new Date()}
                error={errors.dob}
                required
              />

              {alertMsg && (
                <div className="ecs-alert">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  <div>
                    <div className="ecs-alert__desc">{alertMsg.title}</div>
                    {/* <div className="ecs-alert__desc">{alertMsg.desc}</div> */}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="ecs-button btn primary-button regular" disabled={loading}>
                  {loading ? "Validating..." : "Validate policy"}
                </button>
              </div>

              <div className="ecs-trust">
                <span>OTP-protected</span>
                <span>GAIN policyholders only</span>
              </div>
            </form>
          )}

          {/* ---------------- STEP 2 : VERIFY ---------------- */}
          {step === 2 && (
            <div className="ecs-consent__form">
              <button
                type="button"
                className="ecs-back-link"
                onClick={() => setStep(1)}
              >
                &larr; Back
              </button>

              <div className="ecs-otp-to">
                {otpBypassMode ? (
                  <>Enter the one-time password to continue</>
                ) : (
                  <>
                    Code sent to <b>{maskMobile(pendingPolicy?.mobile)}</b>
                  </>
                )}
              </div>

              <div className="ecs-otp-grid">
                {otp.map((digit, pos) => (
                  <input
                    key={`otp-${pos}`}
                    ref={(el) => (otpRefs.current[pos] = el)}
                    data-pos={pos}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    disabled={otpLoading}
                    className={digit ? "filled" : ""}
                    onChange={handleOtpChange}
                    onKeyDown={handleOtpKeyDown}
                    onPaste={handleOtpPaste}
                    aria-label={`OTP digit ${pos + 1}`}
                  />
                ))}
              </div>

              {otpError && <div className="error-text">{otpError}</div>}

              <div className="ecs-resend">
                {resendSeconds > 0 ? (
                  <span>
                    Resend code in <b>0:{resendSeconds < 10 ? `0${resendSeconds}` : resendSeconds}</b>
                  </span>
                ) : (
                  <button type="button" className="ecs-link-button" onClick={handleResendOtp} disabled={otpLoading}>
                    Resend code
                  </button>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="ecs-button btn primary-button regular"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading}
                >
                  {otpLoading ? "Verifying..." : "Verify & continue"}
                </button>
              </div>
            </div>
          )}

          {/* ---------------- STEP 3 : DETAILS + CONSENT ---------------- */}
          {step === 3 && policy && (
            <div className="ecs-consent__form">
                  <div className="ecs-kv">
                    <div className="ecs-kv__cell">
                      <div className="ecs-kv__k">Policyholder</div>
                      <div className="ecs-kv__v">{policy.policyholderName || "—"}</div>
                    </div>
                    <div className="ecs-kv__cell">
                      <div className="ecs-kv__k">Policy number</div>
                      <div className="ecs-kv__v mono">{policy.policyNumber || "—"}</div>
                    </div>
                    <div className="ecs-kv__cell">
                      <div className="ecs-kv__k">Plan</div>
                      <div className="ecs-kv__v">{policy.planName || "—"}</div>
                    </div>
                    <div className="ecs-kv__cell">
                      <div className="ecs-kv__k">Policy status</div>
                      <div className="ecs-kv__v">
                        <span className="ecs-badge">{policy.policyStatus || "In force"}</span>
                      </div>
                    </div>
                    <div className="ecs-kv__cell">
                      <div className="ecs-kv__k">Next premium due</div>
                      <div className="ecs-kv__v mono">{policy.nextPremiumDate || "—"}</div>
                    </div>
                    <div className="ecs-kv__cell">
                      <div className="ecs-kv__k">Registered mobile</div>
                      <div className="ecs-kv__v mono">{maskMobile(policy.mobile)}</div>
                    </div>
                  </div>

                  <div className="ecs-offset">
                    <p className="ecs-offset__lead">
                      Of your <b>{formatCurrency(policy.premiumPayable)} </b>Installment Premium,{" "}
                      <b>{formatCurrency(policy.incomeApplied)}</b> is covered by your Guaranteed Income. Only{" "}
                      <b>{formatCurrency(Number(policy.premiumPayable || 0) - Number(policy.incomeApplied || 0))}</b> will be debited.
                    </p>

                  

                    <div className="ecs-legend">
                      <div><span className="ecs-legend__sw cov" />Covered by Guaranteed Income</div>
                      <div><span className="ecs-legend__sw res" />Debited via ECS</div>
                    </div>

                    <div className="ecs-figs">
                      <div className="ecs-fig">
                        <div className="ecs-fig__l">Instalment Premium</div>
                        <div className="ecs-fig__v">{formatCurrency(policy.premiumPayable)}</div>
                      </div>
                      <div className="ecs-fig credit">
                        <div className="ecs-fig__l">Guaranteed Income applied</div>
                        <div className="ecs-fig__v">{formatCurrency(policy.incomeApplied)}</div>
                      </div>
                      <div className="ecs-fig hero">
                        <div className="ecs-fig__l">Amount to be Debited</div>
                        <div className="ecs-fig__v"> {formatCurrency(Number(policy.premiumPayable || 0) - Number(policy.incomeApplied || 0))}</div>
                      </div>
                    </div>
                  </div>

                  {/* POfFlag "Y" / null / blank: show a message instead of the declaration and submit button. */}
                  {policy.pofFlag === "Y" && (
                    <div className="ecs-alert">
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                      <div>
                        <div className="ecs-alert__desc">
                          Premium Offset consent has already been provided. You cannot submit consent again.
                        </div>
                      </div>
                    </div>
                  )}

                  {!policy.pofFlag && (
                    <div className="ecs-alert">
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                      <div>
                        <div className="ecs-alert__desc">
                          You have opted in for Flexi Wallet. You are not eligible to provide Premium Offset consent.
                        </div>
                      </div>
                    </div>
                  )}

                  {policy.pofFlag && policy.pofFlag !== "Y" && (
                  <form className="ecs-consent-decl" onSubmit={handleSubmitConsent}>
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={agreeChecked}
                        onChange={toggleAgree}
                      />
                      <span>I authorize IndusInd Nippon Life to offset my renewal premium using my applicable Guaranteed Income. I agree to pay only the reduced balance through my registered bank account (ECS).</span>
                    </label>

                    <div className="form-actions">
                      <button
                        type="submit"
                        className="ecs-button btn primary-button regular"
                        disabled={!agreeChecked || loading}
                      >
                        {loading ? "Submitting..." : "Submit consent"}
                      </button>
                    </div>
                  </form>
                  )}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          resetForm();
        }}
        title="Consent recorded"
        type="success"
        btnTitle="Done"
      >
        <p>{successMessage}</p>
      </Modal>

      <Modal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setApiErrorMsg("");
        }}
        title="Something went wrong!"
        type="error"
        btnTitle="Try Again"
      >
        <p>{apiErrorMsg}</p>
      </Modal>

      <Modal
        isOpen={showAlreadyConsentedModal}
        onClose={() => {
          setShowAlreadyConsentedModal(false);
          performSubmit();
        }}
        title="Consent already on file"
        type="success"
        btnTitle="Proceed"
      >
        <p>{ALREADY_CONSENTED_MESSAGE}</p>
      </Modal>

      <Modal
        isOpen={showNearDebitDateModal}
        onClose={() => {
          setShowNearDebitDateModal(false);
          performSubmit();
        }}
        title="Close to your ECS debit date"
        type="success"
        btnTitle="Proceed"
      >
        <p>{NEAR_DEBIT_DATE_MESSAGE}</p>
      </Modal>
    </div>
  );
};

/** Formats a number as an Indian Rupee amount, e.g. 85000 -> "₹85,000". */
function formatCurrency(value) {
  const n = Number(value) || 0;
  return `₹${n.toLocaleString("en-IN")}`;
}

export default EcsPremiumOffsetConsentForm;
